import * as Styles from "./PendingCampaignsPage.styles.ts";
import {
	CartItemDescription,
	CartItemSku
} from "features/MarketingMaterials/pages/Checkout/components/CartItem/CartItem.styles.ts";
import { useNavigate } from "react-router";
import { PageHeader } from "../components/PageHeader/PageHeader.tsx";
import useMarketingMaterialsCart from "../../hooks/useMarketingMaterialsCart.ts";
import { ViewCampaignsSkeleton } from "./components/Skeletons/ViewCampaignsSkeleton.tsx";
import { CampaignAllocation, useGetCampaignAllocationsQuery } from "features/MarketingMaterials/api/campaignApi.ts";
import { Checkbox, Divider } from "@mui/material";
import { useEffect, useMemo } from "react";
import {
	getCampaignAllocationKey,
	getDisplayCampaignAllocations
} from "features/MarketingMaterials/utils/campaignSelection.ts";

const EMPTY_ALLOCATIONS: CampaignAllocation[] = [];
const sortByExpirationDate = (a: CampaignAllocation, b: CampaignAllocation) => {
	const expirationDiff = new Date(a.expirationDate).getTime() - new Date(b.expirationDate).getTime();
	if (expirationDiff !== 0) return expirationDiff;
	return (a.jobName ?? "").localeCompare(b.jobName ?? "");
};

export const PendingCampaignsPage = () => {
	const { cartData, selectedCampaignKeys, setCampaignItems, toggleCampaignSelection, clearCampaignSelection } =
		useMarketingMaterialsCart();
	const { data, isLoading: isPendingCampaignsLoading } = useGetCampaignAllocationsQuery();
	const { storeAllocations, repKitAllocations } = useMemo(() => {
		const stores: CampaignAllocation[] = [];
		const repKits: CampaignAllocation[] = [];

		getDisplayCampaignAllocations(data ?? EMPTY_ALLOCATIONS).forEach((item) => {
			if (item.storeAllocationId) stores.push(item);
			else repKits.push(item);
		});

		return {
			storeAllocations: stores.sort(sortByExpirationDate),
			repKitAllocations: repKits.sort(sortByExpirationDate)
		};
	}, [data]);
	const displayAllocations = useMemo(
		() => [...storeAllocations, ...repKitAllocations],
		[repKitAllocations, storeAllocations]
	);
	const navigate = useNavigate();
	useEffect(() => setCampaignItems(displayAllocations), [displayAllocations, setCampaignItems]);
	const shipSelected = () => navigate("/marketing-materials/campaign-shipping");
	const selectCampaignAndShip = (key: string) => {
		clearCampaignSelection();
		navigate("/marketing-materials/campaign-shipping", {
			state: { campaignSelectionKey: key }
		});
	};

	const renderCampaignItem = (item: CampaignAllocation) => {
		const key = getCampaignAllocationKey(item);
		const primaryLabel =
			item.storeNameNumber && item.campaignName
				? `${item.storeNameNumber} - ${item.campaignName}`
				: item.storeNameNumber || item.jobName || item.campaignName || "Rep Kit";
		const secondaryLabel = item.itemLaunchKitType || item.itemKitName || "";
		return (
			<Styles.CampaignItemWrapper
				key={key}
				data-testid="campaign-item-wrapper"
				role="button"
				tabIndex={0}
				onClick={() => selectCampaignAndShip(key)}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						e.preventDefault();
						selectCampaignAndShip(key);
					}
				}}
			>
				<Checkbox
					checked={selectedCampaignKeys.includes(key)}
					onChange={() => toggleCampaignSelection(key)}
					onClick={(e) => e.stopPropagation()}
					onKeyDown={(e) => e.stopPropagation()}
					inputProps={{ "aria-label": `Select ${item.campaignName}` }}
				/>
				<div>
					<CartItemSku>{primaryLabel}</CartItemSku>
					{secondaryLabel && <CartItemDescription>{secondaryLabel}</CartItemDescription>}
				</div>
				<div>
					<p>Qty</p>
					<p>{item.quantity}</p>
				</div>
				<div>
					<p>Expiration Date</p>
					<p>{new Date(item.expirationDate).toLocaleDateString()}</p>
				</div>
				<Styles.CampaignItemChevron />
			</Styles.CampaignItemWrapper>
		);
	};

	return (
		<>
			{isPendingCampaignsLoading ? (
				<ViewCampaignsSkeleton />
			) : (
				<Styles.PendingCampaignsPageWrapper>
					<PageHeader cartData={cartData} />
					<Styles.PendingCampaignsContainer>
						<Styles.PendingCampaignsHeaderItem>
							<h3>Pending Campaign Orders</h3>
						</Styles.PendingCampaignsHeaderItem>
						{storeAllocations.map(renderCampaignItem)}
						{Boolean(storeAllocations.length && repKitAllocations.length) && <Divider />}
						{repKitAllocations.map(renderCampaignItem)}
						<Styles.ShipSelectedButton
							variant="outlined"
							disabled={!selectedCampaignKeys.length}
							onClick={shipSelected}
						>
							Ship Selected Kits to Single Location
						</Styles.ShipSelectedButton>
					</Styles.PendingCampaignsContainer>
				</Styles.PendingCampaignsPageWrapper>
			)}
		</>
	);
};
